/**
 * Asset State Atoms
 *
 * Jotai atoms for asset filters, selection, and view preferences.
 */

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { AssetFilter, AssetType, AssetStatus } from '@/schemas/asset';

// Filter Atoms

export const assetFiltersAtom = atomWithStorage<AssetFilter>(
  'asset-filters',
  { factoryId: '', page: 1, limit: 20 },
  undefined,
  { getOnInit: true }
);

export const assetSearchQueryAtom = atom<string>('');
export const assetTypeFilterAtom = atom<AssetType | undefined>(undefined);
export const assetStatusFilterAtom = atom<AssetStatus | undefined>(undefined);

/** Combined filters for API query */
export const assetQueryFiltersAtom = atom(get => {
  const baseFilters = get(assetFiltersAtom);
  const searchQuery = get(assetSearchQueryAtom);
  const typeFilter = get(assetTypeFilterAtom);
  const statusFilter = get(assetStatusFilterAtom);

  return {
    ...baseFilters,
    ...(searchQuery && { search: searchQuery }),
    ...(typeFilter && { type: typeFilter }),
    ...(statusFilter && { status: statusFilter }),
  } as AssetFilter;
});

// Selection Atoms

export const selectedAssetIdAtom = atomWithStorage<string | null>('selected-asset-id', null);
export const selectedAssetIdsAtom = atom<Set<string>>(new Set<string>());

export const isAssetSelectedAtom = atom(get => (assetId: string): boolean => {
  return get(selectedAssetIdsAtom).has(assetId);
});

export const selectedAssetCountAtom = atom(get => get(selectedAssetIdsAtom).size);

export const toggleAssetSelectionAtom = atom(null, (get, set, assetId: string) => {
  const selected = get(selectedAssetIdsAtom);
  const newSelected = new Set(selected);
  if (newSelected.has(assetId)) {
    newSelected.delete(assetId);
  } else {
    newSelected.add(assetId);
  }
  set(selectedAssetIdsAtom, newSelected);
});

export const clearAssetSelectionAtom = atom(null, (_get, set) => {
  set(selectedAssetIdsAtom, new Set());
});

export const selectAllAssetsAtom = atom(null, (_get, set, assetIds: string[]) => {
  set(selectedAssetIdsAtom, new Set(assetIds));
});

// View Preferences

export const assetViewModeAtom = atomWithStorage<'list' | 'grid'>('asset-view-mode', 'list');

// Reset Helpers

export const resetAssetStateAtom = atom(null, (_get, set) => {
  set(selectedAssetIdAtom, null);
  set(assetSearchQueryAtom, '');
  set(selectedAssetIdsAtom, new Set<string>());
});

export const resetAssetFiltersAtom = atom(null, (get, set) => {
  const currentFilters = get(assetFiltersAtom) as AssetFilter;
  set(assetFiltersAtom, {
    factoryId: currentFilters.factoryId,
    page: 1,
    limit: 20,
  });
  set(assetSearchQueryAtom, '');
});

export const resetAssetSelectionAtom = atom(null, (_get, set) => {
  set(selectedAssetIdAtom, null);
  set(selectedAssetIdsAtom, new Set<string>());
});
